import DaumPostcodeEmbed from "react-daum-postcode";
import React, { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useLazyQuery } from "@apollo/client";
import LoadingSpinnerFull from "../../../../components/common/loading-spinner/loading-spinner-full";
import CustomMapMarker2 from "../../../../components/common/custom-map-marker-2";
import Header from "../../../../components/common/header";
import LoadingSpinnerInline from "../../../../components/common/loading-spinner/loading-spinner-inline";
import clsx from "clsx";
import { track } from "@amplitude/analytics-browser";
import ResponsiveCDNImage from "../../../../components/common/image/responsive-cdn-image";
import {
  GET_SELLER_STORE,
  UPDATE_STORE,
  CREATE_STORE,
  GET_COORDS,
} from "../../api/seller-store";

type Store = {
  id: number;
  img?: string;
  title: string;
  address: string;
  detailAddress?: string;
  navigationInfo?: string;
  contactNumber: string;
  closingHours: string;
  lat: number;
  lng: number;
  signupState: string;
};

const StoreManagement: React.FC = () => {
  const [store, setStore] = useState<Store | null>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false); // 모달 상태
  const [lat, setLat] = useState(0);
  const [lng, setLng] = useState(0);
  const [imgFile, setImgFile] = useState<File | null>(null); // 이미지 파일 상태 추가

  // Fetch seller's store using Apollo Client's useQuery hook
  const { loading, error } = useQuery(GET_SELLER_STORE, {
    onCompleted: (data) => {
      setStore(data.getSellerStore);
      setLat(data.getSellerStore.lat);
      setLng(data.getSellerStore.lng);
    },
    fetchPolicy: "network-only",
  });
  const [updateStore] = useMutation(UPDATE_STORE);
  const [createStore] = useMutation(CREATE_STORE);

  const handleAddStore = () => {
    if (store?.signupState === "승인" || store?.signupState === "영업") {
      setIsEditing(true);
    }
  };

  const handleSaveStore = async (storeInfo: Store) => {
    if (store) {
      await updateStore({
        variables: {
          id: store.id, // Store ID를 기반으로 업데이트
          title: storeInfo.title,
          address: storeInfo.address,
          detailAddress: storeInfo.detailAddress,
          navigationInfo: storeInfo.navigationInfo,
          contactNumber: storeInfo.contactNumber,
          closingHours: storeInfo.closingHours,
          lat,
          lng,
          img: imgFile, // 이미지 파일 전달
        },
      }).then(() => {
        setStore(storeInfo);
        setIsEditing(false);
        if (storeInfo.signupState === "승인") {
          window.location.reload();
        }
      });
    } else {
      // Create new store
      createStore({
        variables: {
          title: storeInfo.title,
          address: storeInfo.address,
          contactNumber: storeInfo.contactNumber,
          closingHours: storeInfo.closingHours,
          lat,
          lng,
          img: imgFile, // 이미지 파일 전달
        },
      }).then((response) => {
        setStore(response.data.createStore);
        setIsEditing(false);
        window.location.reload();
      });
    }
  };
  const handleOpenModal = () => setIsModalOpen(true); // 모달 열기

  const handleSelectAddress = (lng: number, lat: number) => {
    setLng(lng);
    setLat(lat);
    setIsModalOpen(false); // 모달 닫기
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImgFile(file); // 파일을 상태에 저장
    }
  };

  if (loading) return <LoadingSpinnerFull />;
  if (error) return <p>Error: {error.message}</p>;

  if (
    (store?.signupState === "승인" || store?.signupState === "영업") &&
    !store &&
    !isEditing
  ) {
    return (
      <div className="flex flex-col items-center justify-start min-h-[100dvh] bg-gray-100">
        <Header title="매장정보" showBackButton />
        <p className="text-lg mb-6 text-gray-700 mt-12">
          매장을 등록해보세요!!
        </p>
        <button
          onClick={handleAddStore}
          className="px-6 py-3 bg-blue-500 text-white font-semibold rounded-lg shadow-md hover:bg-blue-600 transition duration-300"
        >
          매장 추가하기
        </button>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* 요청 상태일 때는 뒤로가기 버튼 없음 */}
      <Header title="매장정보" showBackButton />
      <div className="p-6">
        {isEditing ? (
          <StoreEditForm
            onSave={handleSaveStore}
            handleOpenModal={handleOpenModal}
            onCancel={() => setIsEditing(false)}
            store={store}
            setLng={setLng}
            setLat={setLat}
            lng={lng}
            lat={lat}
            handleImageChange={handleImageChange} // 이미지 파일 핸들러 전달
          />
        ) : (
          <div className="max-w-xl mx-auto bg-white p-6 rounded-lg shadow-md">
            {store?.img && (
              <div className="mb-6">
                <ResponsiveCDNImage
                  src={store.img}
                  alt={`${store?.title} 매장 이미지`}
                  className="w-full h-auto rounded-lg"
                />
              </div>
            )}

            <div className="mb-4">
              <p className="font-bold  text-gray-900 text-md mb-1">매장이름</p>
              <p className="text-black">{store?.title}</p>
            </div>

            <div className="mb-4">
              <p className="font-bold  text-gray-900 text-md mb-1">주소</p>
              <p className="text-black">{store?.address}</p>
              {store?.detailAddress && (
                <p className="text-black mt-1">{store.detailAddress}</p>
              )}
            </div>

            {store?.navigationInfo && (
              <div className="mb-4">
                <p className="font-bold text-gray-900 text-md mb-1">
                  찾아오시는 길
                </p>
                <p className="text-black whitespace-pre-wrap">
                  {store.navigationInfo}
                </p>
              </div>
            )}

            <div className="mb-4">
              <p className="font-bold  text-gray-900 text-md mb-1">매장번호</p>
              <p className="text-black">{store?.contactNumber}</p>
            </div>

            <div className="mb-4">
              <p className="font-bold  text-gray-900 text-md mb-1">영업 시간</p>
              <p>(변경 시 실시간 판매 팩에 모두 적용됩니다)</p>
              <p className="text-black">~ {store?.closingHours}</p>
            </div>

            <button
              onClick={() => setIsEditing(true)}
              className="w-full py-3 mt-4 bg-blue-500 text-white font-semibold rounded-lg shadow-md hover:bg-blue-600 transition duration-300"
            >
              매장 정보 수정
            </button>
          </div>
        )}
        {lat && lng && isModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-white py-6 rounded-lg shadow-lg w-[80%]">
              <h2 className="text-xl font-bold mb-4">상세 주소 선택</h2>
              <Map onSelectAddress={handleSelectAddress} lng={lng} lat={lat} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// 매장 수정 폼 컴포넌트
type StoreEditFormProps = {
  onSave: (storeInfo: Store) => Promise<void>;
  onCancel: () => void;
  store: Store | null;
  handleOpenModal: () => void;
  setLng: (data: number) => void;
  setLat: (data: number) => void;
  lng: number;
  lat: number;
  handleImageChange: (e: React.ChangeEvent<HTMLInputElement>) => void; // 이미지 파일 핸들러 추가
};

const StoreEditForm: React.FC<StoreEditFormProps> = ({
  onSave,
  handleOpenModal,
  onCancel,
  store,
  setLng,
  setLat,
  lng,
  lat,
  handleImageChange, // 이미지 파일 핸들러 추가
}) => {
  const [title, setTitle] = useState(store?.title || "");
  const [address, setAddress] = useState(store?.address || "");
  const [detailAddress, setDetailAddress] = useState(
    store?.detailAddress || ""
  );
  const [navigationInfo, setNavigationInfo] = useState(
    store?.navigationInfo || ""
  );
  const [contactNumber, setContactNumber] = useState(
    store?.contactNumber || ""
  );
  const [isSpecificSet, setIsSpecificSet] = useState(false);
  const [closingHours, setClosingHours] = useState(store?.closingHours || "");
  const [getCoordsFromAddress] = useLazyQuery(GET_COORDS);
  const [isLoading, setIsLoading] = useState(false);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const handleSubmit = async () => {
    if (!title || !address || !closingHours || !lat || !lng) {
      setErrorMessage("필수 항목들을 입력해주세요. (*표시 항목들)");
      return;
    }

    // 영업 종료 시간이 변경되었을 때
    if (store?.closingHours !== closingHours) {
      const isConfirmed = window.confirm(
        "영업 종료 시간이 변경되면 실시간 판매 팩에 모두 바로 적용됩니다! 변경하시겠습니까?\n(이후 시간 선택시 판매 시간이 늘어납니다)"
      );
      track("사장님 매장 마감시간 변경 확인 선택");
      if (!isConfirmed) {
        return;
      }
    }

    setIsLoading(true);

    await onSave({
      id: store?.id || 0,
      title,
      address,
      detailAddress,
      navigationInfo,
      contactNumber,
      closingHours,
      lng,
      lat,
      signupState: store?.signupState || "",
    });

    setIsLoading(false);

    setErrorMessage(null); // 에러 메시지 초기화
  };

  const handleAddressComplete = (data: any) => {
    let fullAddress = data.address;

    setAddress(fullAddress);
    getCoordsFromAddress({
      variables: {
        address: fullAddress,
      },
    }).then((response) => {
      setLng(response.data.getCoords.lng);
      setLat(response.data.getCoords.lat);
    });
  };
  const [openPostModal, setOpenPostModal] = useState(false);
  const handleAddressClick = () => {
    setOpenPostModal(true);
    // openPostCode({ onComplete: handleAddressComplete });
  };

  const handleSpecificAddressClick = () => {
    // location modal open
    handleOpenModal();
    setIsSpecificSet(true);
  };

  return (
    <div className="max-w-xl mx-auto bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-2xl font-bold mb-6 text-center">매장 정보 수정</h2>

      <div className="mb-4">
        <label className="block text-gray-700 font-semibold">*매장 이름</label>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-400 transition duration-200"
        />
      </div>

      <div className="mb-2">
        <label className="block text-gray-700 font-semibold">*매장 주소</label>
        <div
          onClick={handleAddressClick}
          className="w-full px-4 py-4 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-400 transition duration-200"
        >
          {address}
        </div>
      </div>
      {openPostModal && (
        <DaumPostcodeEmbed
          onComplete={handleAddressComplete}
          scriptUrl="//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js"
        />
      )}

      <div className="mb-4">
        <input
          value={detailAddress}
          onChange={(e) => setDetailAddress(e.target.value)}
          placeholder="상세 주소를 입력해주세요"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-400 transition duration-200"
        />
      </div>

      <div className="mb-4">
        <label className="block text-gray-700 font-semibold">
          찾아오는 길 안내 (선택)
        </label>
        <textarea
          value={navigationInfo}
          onChange={(e) => setNavigationInfo(e.target.value)}
          placeholder="길찾기가 어려울 경우 참고할 수 있는 메세지 ex) 00 오른쪽으로 가서 주차장 옆에 있습니다."
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-400 transition duration-200"
          rows={3}
        />
      </div>

      <div className="flex flex-col mb-4">
        <label className="block text-gray-700 font-semibold">
          *지도에서 한 번 더 설정
        </label>
        <div className="flex gap-2">
          <div
            onClick={handleSpecificAddressClick}
            className="text-blue-500 w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-400 transition duration-200"
          >
            {isSpecificSet ? "완료" : "설정하기"}
          </div>
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-gray-700 font-semibold">매장번호</label>
        <input
          value={contactNumber}
          onChange={(e) => setContactNumber(e.target.value)}
          placeholder="000-0000-0000"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-400 transition duration-200"
        />
      </div>

      <div className="mb-4">
        <label className="block text-gray-700 font-semibold">
          *영업 종료 시간
        </label>
        <input
          type="time"
          value={closingHours}
          onChange={(e) => setClosingHours(e.target.value)}
          placeholder="21:00"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-400 transition duration-200"
        />
      </div>

      <div className="mb-4">
        <label className="block text-gray-700 font-semibold">매장 이미지</label>
        <input
          type="file"
          accept="image/*"
          onChange={handleImageChange} // 이미지 파일 변경 핸들러
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-400 transition duration-200"
        />
      </div>

      {errorMessage && (
        <div className="text-red-500 text-sm mb-4">{errorMessage}</div>
      )}

      <div className="flex justify-between mt-6">
        <button
          onClick={onCancel}
          className="px-4 py-2 bg-gray-400 text-white rounded-lg active:bg-gray-500 transition duration-200"
        >
          취소
        </button>
        <button
          onClick={handleSubmit}
          className={clsx(
            "flex items-center justify-center px-4 py-2 bg-blue-500 text-white rounded-lg active:bg-blue-600 transition duration-200 w-16",
            isLoading && "pointer-events-none opacity-80"
          )}
          disabled={isLoading}
        >
          {isLoading ? <LoadingSpinnerInline /> : "저장"}
        </button>
      </div>
    </div>
  );
};

export default StoreManagement;

const Map = ({
  onSelectAddress,
  lat,
  lng,
}: {
  onSelectAddress: (lat: number, lng: number) => void;
  lng: number;
  lat: number;
}) => {
  const mapElement = useRef<HTMLDivElement | null>(null);

  const { naver } = window;
  let map: naver.maps.Map;
  // 지도가 이동하였을 때 가려진 부분의 마커는 숨기고
  // 노출된 부분의 마커를 표시하는 작업을 하기 위해 map 인스턴스를 state로 관리
  const [newMap, setNewMap] = useState<naver.maps.Map | null>(null);

  const center = new naver.maps.LatLng(lat, lng);
  // [지도를 그리는 effect]
  useEffect(() => {
    if (!mapElement.current || !naver) {
      return;
    }

    const mapOptions: naver.maps.MapOptions = {
      center,
      zoom: 15,
      minZoom: 7,
      maxZoom: 19,
      zoomControl: true,
      zoomControlOptions: {
        style: naver.maps.ZoomControlStyle.SMALL,
        position: naver.maps.Position.TOP_RIGHT,
      },
      mapDataControl: false,
      scaleControl: false,
    };

    //설정해놓은 옵션을 바탕으로 지도 생성
    // eslint-disable-next-line react-hooks/exhaustive-deps
    map = new naver.maps.Map(mapElement.current, mapOptions);
    setNewMap(map);
    const marker = new naver.maps.Marker({
      position: center,
      map,
      clickable: true,
      // [마커 커스터마이징]
      icon: {
        //html element를 반환하는 CustomMapMarker 컴포넌트 할당
        content: CustomMapMarker2(),
        //마커의 기준위치 지정
        anchor: new naver.maps.Point(25, 58),
      },
    });

    // [마커는 항상 맵 중앙에]
    naver.maps.Event.addListener(map, "drag", function (e) {
      const centerCoord = map.getCenter();
      marker.setPosition(centerCoord);
    });
  }, []);

  function onClickClose() {
    const center = newMap?.getCenter();

    const lat = center?.y as number;
    const lng = center?.x as number;
    onSelectAddress(lng, lat);
  }
  return (
    <div className="w-[100%] h-[400px]">
      <div
        ref={mapElement}
        id="map"
        style={{ width: "100%", height: 420 }}
      ></div>
      <button
        onClick={() => onClickClose()}
        className="mt-4 px-4 py-2 bg-gray-400 text-white rounded"
      >
        완료
      </button>
    </div>
  );
};
